import type { PriceType } from '@/lib/types';

/**
 * The locale every Intl formatter below uses, set from the signed-in user's Language
 * preference (falling back to the browser default when unset). Module-level so a
 * preference change is reflected everywhere prices, money and times are formatted.
 */
let activeLocale: string | undefined;
export function setFormatLocale(locale: string | undefined): void {
  activeLocale = locale || undefined;
}

/**
 * Prices are stored in minor units (cents/centavos), because floats cannot hold
 * money. Divide only here, at the edge, where it becomes a string.
 */
export function formatPrice(
  priceType: PriceType,
  min: number | null,
  max: number | null,
  currency: string,
): string {
  if (priceType === 'QUOTE' || min === null) return 'Request a quote';

  const money = (cents: number) =>
    new Intl.NumberFormat(activeLocale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(cents / 100);

  if (priceType === 'RANGE' && max !== null && max !== min) return `${money(min)} – ${money(max)}`;
  if (priceType === 'HOURLY') return `${money(min)}/hr`;
  return money(min);
}

/** A bare amount in minor units — for totals (earnings), not listing price ranges. */
export function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat(activeLocale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours} hr` : `${hours} hr ${rest} min`;
}

export function formatDistance(km: number | null): string | null {
  if (km === null) return null;
  return km < 1 ? `${Math.round(km * 1000)} m away` : `${km.toFixed(1)} km away`;
}

/** "4.7 (42)" — and nothing at all when nobody has reviewed yet. */
export function formatRating(avg: number, count: number): string | null {
  if (count === 0) return null;
  return `${avg.toFixed(1)} (${count})`;
}

const RELATIVE_UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['week', 7 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
];

/** "3 days ago", "2 months ago" — anything under a minute reads as "just now". */
export function timeAgo(iso: string, now = Date.now()): string {
  const diff = new Date(iso).getTime() - now;
  const abs = Math.abs(diff);
  if (abs < 60 * 1000) return 'just now';
  const rtf = new Intl.RelativeTimeFormat(activeLocale, { numeric: 'auto' });
  for (const [unit, ms] of RELATIVE_UNITS) {
    if (abs >= ms) return rtf.format(Math.round(diff / ms), unit);
  }
  return 'just now';
}

/**
 * Reads a typed amount into minor units.
 *
 * Prices cross the wire as integer cents, and this is the only place a decimal ever
 * becomes one. `15.99 * 100` is `1598.9999999999998` in binary floating point, so the
 * rounding is load-bearing, not defensive: without it every third price a provider types
 * is stored a cent short.
 *
 * Returns null for blank input — a QUOTE listing has no price, and an empty field is not
 * the same as zero.
 */
export function toMinorUnits(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;

  const amount = Number(trimmed);
  if (!Number.isFinite(amount) || amount < 0) return null;
  return Math.round(amount * 100);
}

/** The inverse, for prefilling a form from what the server stored. */
export function toMajorUnits(cents: number | null): string {
  if (cents === null) return '';
  return (cents / 100).toFixed(2).replace(/\.00$/, '');
}
